/**
 * Module Handler Compiler
 *
 * Compiles handler strings (inline JavaScript function bodies) into
 * callable functions. Handlers execute in a restricted scope with
 * only the ModuleAPI available — no access to window, document,
 * fetch, or other globals.
 *
 * Handler formats:
 *   - Inline body: "return `Weather in ${args}: sunny`"
 *   - Multi-line:  "const loc = args || 'unknown';\nreturn `Weather in ${loc}: rainy`"
 *
 * Command handlers receive (api, ctx) and must return a string.
 * Event handlers receive (api, payload) and return void.
 */
// =============================================================================
// BLOCKED GLOBALS — prevent module code from accessing dangerous APIs
// =============================================================================
// Note: 'import' is a keyword and 'eval'/'arguments' are restricted in
// strict mode — none of these can be used as Function() parameter names.
// We shadow everything else as undefined to block access.
const BLOCKED_GLOBALS = [
    'window', 'document', 'globalThis', 'self',
    'fetch', 'XMLHttpRequest', 'WebSocket', 'EventSource',
    'localStorage', 'sessionStorage', 'indexedDB',
    'importScripts',
    'process', 'require',
    'setTimeout', 'setInterval', 'requestAnimationFrame',
];
/** Builds parameter list that shadows dangerous globals with undefined */
function blockedParams() {
    return BLOCKED_GLOBALS.join(', ');
}
// =============================================================================
// COMPILER
// =============================================================================
/**
 * Compile an inline handler body string into a callable command handler.
 *
 * The handler body is wrapped in a Function constructor with (api, ctx) params
 * and dangerous globals shadowed. The body can use:
 *   - `api` — the ModuleAPI object (bestiary, combat, chat, dice, storage, etc.)
 *   - `ctx` — CommandContext with { campaignId, userId, isGM, args }
 *   - Standard JS (Math, Date, JSON, Array, Object, String, Number, etc.)
 *
 * Must return a string (the chat message to display).
 */
export function compileCommandHandler(handlerBody, moduleId) {
    try {
        // eslint-disable-next-line no-new-func
        const fn = new Function('api', 'ctx', 
        // Shadow dangerous globals
        blockedParams(), 
        // Wrap in async to support await in handler bodies
        `"use strict";\nreturn (async () => {\n${handlerBody}\n})();`);
        return async (api, ctx) => {
            try {
                const result = await fn(api, ctx);
                return typeof result === 'string' ? result : String(result ?? '');
            }
            catch (err) {
                const msg = err instanceof Error ? err.message : String(err);
                console.error(`[module-handler] Command error in ${moduleId}:`, err);
                return `Module error: ${msg}`;
            }
        };
    }
    catch (err) {
        console.error(`[module-handler] Failed to compile command for ${moduleId}:`, err);
        return () => `Failed to compile handler for module ${moduleId}`;
    }
}
/**
 * Compile an inline handler body string into a callable event handler.
 *
 * Same sandbox as command handlers, but:
 *   - Receives (api, payload) instead of (api, ctx)
 *   - Returns void (side-effects only — use api.sendMessage, api.emit, etc.)
 */
export function compileEventHandler(handlerBody, moduleId) {
    try {
        // eslint-disable-next-line no-new-func
        const fn = new Function('api', 'payload', blockedParams(), `"use strict";\nreturn (async () => {\n${handlerBody}\n})();`);
        return async (api, payload) => {
            try {
                await fn(api, payload);
            }
            catch (err) {
                console.error(`[module-handler] Event error in ${moduleId}:`, err);
            }
        };
    }
    catch (err) {
        console.error(`[module-handler] Failed to compile event handler for ${moduleId}:`, err);
        return () => { };
    }
}
//# sourceMappingURL=module-handler.js.map